import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import { t, Trans } from '@lingui/macro'
import { cx } from '@emotion/css'

import { TopicVersionType } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import UserDetail from '../../../components/common/user/user-detail/UserDetail'
import IconPencil from '../../../components/common/icon/IconPencil'
import IconActionRedo from '../../../components/common/icon/IconActionRedo'
import IconTrash from '../../../components/common/icon/IconTrash'
import { Checkbox } from '../../../components/common/form/checkbox/Checkbox'
import { TableCol, TableRow } from '../../../components/common/table/Table'
import { TopicSmallMarbleRating } from '../../../components/common/marble-rating/TopicSmallMarbleRating'
import { AccessibilityLabel } from '../../../components/common/AccessibilityLabel'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { DuplicateResourceButton } from './DuplicateResourceButton'
import { StatusDraftGrey } from 'front-end/src/components/common/status/Status'

import './ResourceListItem.less'
import { videoAssessmentTopicVersionTypesForFE } from '../../topic-detail-page/StudentTopicDetail'
import { ButtonOutline, Flex } from 'front-end/src/components'
import { IconCamera } from '../../../components/common/icon/IconCamera'
import { SortableHandle } from 'react-sortable-hoc'
import IconDrag from 'front-end/src/components/common/icon/IconDrag'
import { ResourceItemActionButton } from './ResourceItemActionButton'

export type ResourceListItemCallback = (ResourceListItemCallbackArgs) => void

export interface IResourceListItemCallbackArgs {
  id: number
  draftVersion: {
    id: number
    title: string
  }
  currentTopicVersion: null | {
    id: number
    title: string
  }
}

export interface IResourceListItemProps {
  id: number
  author: { display_name: string; image_url: string; id: number }
  viewsCount: number
  rating: number
  currentTitle: string
  numberRated: number
  draftVersion: {
    id: number
    title: string
  }
  currentTopicVersion: null | {
    id: number
    title: string
    topic_type: TopicVersionType
  }
  isSelected: boolean
  order: number
  showSelection: boolean
  onEdit: ResourceListItemCallback
  onMove: ResourceListItemCallback
  onCopy: ResourceListItemCallback
  onDelete: ResourceListItemCallback
  refetchTopics: () => Promise<any>
  onSelect?: ResourceListItemCallback
  onDeselect?: ResourceListItemCallback
}

const DragHandle = SortableHandle(() => (
  <ResourceItemActionButton
    icon={<IconDrag position="relative" top="1px" />}
    hint={t`Drag`}
    aria-label={t`Drag`}
  />
))

export const ResourceListItem: FC<IResourceListItemProps> = (props) => {
  const cssClasses = cx('ResourceListItem', {
    draft: !props.currentTopicVersion
  })

  return (
    <TableRow className={cssClasses}>
      {props.showSelection === true && (
        <TableCol collapsing className="selecting">
          {props.currentTopicVersion && (
            <AccessibilityLabel text={t`Selected`}>
              <Checkbox
                onChange={(e) => {
                  const { checked } = e.currentTarget
                  if (checked && props.onSelect) {
                    props.onSelect(props)
                  } else if (!checked && props.onDeselect) {
                    props.onDeselect(props)
                  }
                }}
                isChecked={!!props.isSelected}
              />
            </AccessibilityLabel>
          )}
        </TableCol>
      )}
      <TableCol className="ResourceListItem__title">
        <span
          className="ResourceListItem__title-link"
          onClick={() => {
            props.onEdit(props)
          }}
        >
          {props.currentTitle}
        </span>
        {!props.currentTopicVersion && <StatusDraftGrey ml="12px" />}
      </TableCol>
      <TableCol className="ResourceListItem__author">
        <Link to={linkBuilder.userDetail(props.author.id)}>
          <UserDetail
            display_name={props.author.display_name}
            imageUrl={props.author.image_url}
            size="small"
          />
        </Link>
      </TableCol>
      <TableCol className="ResourceListItem__rating">
        <Flex justify="flex-end" align="center">
          {props.numberRated > 0 && (
            <TopicSmallMarbleRating
              topicId={props.id}
              rating={props.rating}
              numberRated={props.numberRated}
            />
          )}
          {props.currentTopicVersion?.topic_type &&
            videoAssessmentTopicVersionTypesForFE.includes(
              props.currentTopicVersion?.topic_type
            ) && (
              <Link to={linkBuilder.videoAssessmentGradeBookUrl(props.id)}>
                <ButtonOutline
                  size="sm"
                  px="8px"
                  ml="8px"
                  leftIcon={<IconCamera />}
                >
                  <Trans>Gradebook</Trans>
                </ButtonOutline>
              </Link>
            )}
        </Flex>
      </TableCol>
      <TableCol className="ResourceListItem__actions" collapsing>
        <ResourceItemActionButton
          className="ResourceListItem__actions-edit"
          icon={<IconPencil />}
          hint={t`Edit`}
          aria-label={t`Edit`}
          onClick={() => {
            props.onEdit(props)
          }}
        />
        <ResourceItemActionButton
          className="ResourceListItem__actions-move"
          icon={<IconActionRedo />}
          hint={t`Move`}
          aria-label={t`Move`}
          onClick={() => {
            props.onMove(props)
          }}
        />
        <DuplicateResourceButton
          topicId={props.id}
          refetchTopics={props.refetchTopics}
        />

        <ResourceItemActionButton
          dataTest="DeleteButton"
          className="ResourceListItem__actions-delete"
          icon={<IconTrash />}
          hint={t`Delete`}
          aria-label={t`Delete`}
          onClick={() => {
            props.onDelete(props)
          }}
        />
        <DragHandle />
      </TableCol>
    </TableRow>
  )
}
